import { useEffect, useState, useCallback, useMemo } from "react";
import SimpleMDE from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";
import MarkdownIt from "markdown-it";
import axios from "axios";
import { toast } from "react-toastify";
import { auth } from "../firebase";

const mdParser = new MarkdownIt({ html: true, linkify: true, typographer: true });

export default function AdminMarkdownEditor({ slug = "about", editor = "public" }) {
    const [title, setTitle] = useState("");
    const [md, setMd] = useState("");
    const [image, setImage] = useState("");
    const [olType, setOlType] = useState("");
    const [load, setLoad] = useState(true);
    const [saving, setSaving] = useState(false);
    const [useStatic, setUseStatic] = useState(false);
    const [isStatic, setIsStatic] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    // CTA fields
    const [ctaEnabled, setCtaEnabled] = useState(false);
    const [ctaPosition, setCtaPosition] = useState("after");
    const [ctaHeader, setCtaHeader] = useState("");
    const [ctaDescription, setCtaDescription] = useState("");
    const [ctaButtonLabel, setCtaButtonLabel] = useState("");
    const [ctaButtonLink, setCtaButtonLink] = useState("");

    useEffect(() => {
        const fetchPage = async () => {
            setLoad(true);
            try {
                const token = await auth.currentUser.getIdToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                
                let res;
                if (editor === "public") {
                    res = await axios.get(`/api/content/${slug}`, { headers });
                    setTitle(res.data.title || "");
                    setMd(res.data.markdown || "");
                    setImage(res.data.image || "");
                    setUseStatic(res.data.use_static || false);
                    setIsStatic(res.data.is_static || false);
                    
                    // Load CTA data
                    const cta = res.data.cta || {};
                    setCtaEnabled(cta.enabled || false);
                    setCtaPosition(cta.position || "after");
                    setCtaHeader(cta.header || "");
                    setCtaDescription(cta.description || "");
                    setCtaButtonLabel(cta.button_label || "");
                    setCtaButtonLink(cta.button_link || "");
                } else {
                    res = await axios.get(`/api/member-content/members/${slug}`, { headers });
                    setTitle(res.data.title || "");
                    setMd(res.data.markdown || "");
                    setImage("");
                    setOlType(res.data.ol_type || "d");
                    setUseStatic(false);
                    setIsStatic(false);
                }
            } catch (error) {
                console.error("Error fetching page:", error);
                setTitle(""); 
                setMd(""); 
                setImage(""); 
                setOlType("d"); 
                setUseStatic(false);
                setIsStatic(false);
                setCtaEnabled(false);
                setCtaPosition("after");
                setCtaHeader("");
                setCtaDescription("");
                setCtaButtonLabel("");
                setCtaButtonLink("");
            } finally {
                setLoad(false);
            }
        };
        
        fetchPage();
    }, [slug, editor]);

    const mdeOptions = useMemo(() => ({
        spellChecker: false,
        status: false,
        autofocus: false,
        placeholder: "Start writing…",
        previewRender: txt => mdParser.render(txt)
    }), []);

    const save = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            if (editor === "public") {
                await axios.put(`/api/content/${slug}`, {
                    title: title.trim(),
                    markdown: md,
                    image: image.trim(),
                    use_static: useStatic,
                    cta: {
                        enabled: ctaEnabled,
                        position: ctaPosition,
                        header: ctaHeader.trim(),
                        description: ctaDescription.trim(),
                        button_label: ctaButtonLabel.trim(),
                        button_link: ctaButtonLink.trim(),
                    }
                }, { headers });
            } else {
                await axios.put(`/api/member-content/members/${slug}`, {
                    title: title.trim(),
                    ol_type: olType.trim(),
                    markdown: md
                }, { headers });
            }
            toast.success("Saved!");
        } catch (error) {
            console.error("Error saving page:", error);
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    }, [title, md, olType, image, useStatic, ctaEnabled, ctaPosition, ctaHeader, ctaDescription, ctaButtonLabel, ctaButtonLink, saving, slug, editor]);

    const handleImageUpload = async (file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading(true);

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'pages');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                setImage(response.data.url);
                toast.success('Image uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    if (load) return <p>Loading editor…</p>;

    return (
        <>
            {/* Title Field */}
            <div className="mb-3">
                <label className="form-label fw-bold">Page Title</label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Enter page title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                />
            </div>

            {/* Background Image Field */}
            {editor === "public" &&
                <div className="mb-3">
                    <label className="form-label fw-bold">
                        Background image URL <small className="text-muted">(optional)</small>
                    </label>
                    <input
                        type="url"
                        className="form-control mb-2"
                        placeholder="https://example.com/hero.jpg"
                        value={image}
                        onChange={e => setImage(e.target.value)}
                    />
                    <div className="input-group">
                        <input 
                            type="file" 
                            className="form-control" 
                            accept=".jpg,.jpeg,.png,.heic"
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) handleImageUpload(file);
                            }}
                            disabled={uploading}
                        />
                        <button 
                            className="btn btn-outline-secondary" 
                            type="button"
                            disabled
                        >
                            {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                    </div>
                    {image && (
                        <>
                            <div className="form-text">
                                Leave empty &amp; save to use the default background.
                            </div>
                            <div className="mt-2">
                                <img 
                                    src={image} 
                                    alt="Preview" 
                                    style={{ maxWidth: '300px', maxHeight: '200px', objectFit: 'cover' }}
                                    className="img-thumbnail"
                                />
                            </div>
                        </>
                    )}
                </div>
            }

            {/* Static toggle */}
            {isStatic && (
                <div>
                    <p className="mb-0">Static content with custom functionality is already configured for this page. Would you like to use it?</p>
                    <div className="form-check form-switch mb-3 mt-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="toggleStatic"
                            checked={useStatic}
                            onChange={e => setUseStatic(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="toggleStatic">
                            Use built-in page instead of markdown <small className="text-muted">(Recommended)</small>
                        </label>
                    </div>
                </div>
            )}

            {/* Markdown Editor */}
            {!useStatic && (
                <>
                    <label className="form-label fw-bold">Content</label>
                    <SimpleMDE
                        value={md}
                        onChange={setMd}
                        options={mdeOptions}
                        textareaProps={{ name: "markdown" }}
                    />
                </>
            )}

            {/* Call-to-Action Section */}
            {editor === "public" && (
                <div className="mb-3 mt-4">
                    <hr />
                    <div className="form-check form-switch mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="toggleCta"
                            checked={ctaEnabled}
                            onChange={e => setCtaEnabled(e.target.checked)}
                        />
                        <label className="form-check-label fw-bold" htmlFor="toggleCta">
                            Add Call-to-Action Section
                        </label>
                    </div>

                    {ctaEnabled && (
                        <div className="p-0">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Position</label>
                                <select
                                    className="form-select"
                                    value={ctaPosition}
                                    onChange={e => setCtaPosition(e.target.value)}
                                >
                                    <option value="before">Before content</option>
                                    <option value="after">After content</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Header</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Interested in Joining Our Board?"
                                    value={ctaHeader}
                                    onChange={e => setCtaHeader(e.target.value)}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Description <small className="text-muted">(optional)</small></label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Additional description text..."
                                    value={ctaDescription}
                                    onChange={e => setCtaDescription(e.target.value)}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Button Label</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="e.g., Download Application"
                                    value={ctaButtonLabel}
                                    onChange={e => setCtaButtonLabel(e.target.value)}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">Button Link</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    placeholder="https://example.com/file.pdf"
                                    value={ctaButtonLink}
                                    onChange={e => setCtaButtonLink(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* <div className="mb-3">
                <a
                    className="small-link mt-4 mb-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#stylingCollapse"
                    aria-expanded="false"
                    aria-controls="stylingCollapse"
                >
                    <strong>Styling Options ▾</strong>
                </a>

                <div className="collapse" id="stylingCollapse">
                    <div className="">
                        <div className="mb-3">
                            <label className="form-label fw-bold">Ordered-List Type</label>
                            <select
                                className="form-select"
                                onChange={(e) => setOlType(e.target.value)}
                                value={olType}
                            >
                                <option value="d">(1) Decimal</option>
                                <option value="ua">(B) Upper Alpha</option>
                                <option value="la">(c) Lower Alpha</option>
                                <option value="ur">(III) Upper Roman</option>
                                <option value="lr">(iv) Lower Roman</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div> */}


            <button className="btn btn-admin mt-3" onClick={save} disabled={saving}>
                {saving
                    ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</>
                    : "Save"}
            </button>
        </>
    );
}
